import React, { useEffect, useState } from 'react';
import { Button, ConfirmationModal, PageShell, Table } from 'components';
import {
  AppDispatch,
  deleteInvoiceById,
  fetchInvoices,
  RootState,
  saveInvoiceDocDownload,
} from 'data';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { formatSwissAmount } from 'helpers';

export const Invoices = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { invoices, count, getInvoicesLoading, error } = useSelector(
    (state: RootState) => state.invoices,
  );

  const [currentPage, setCurrentPage] = useState<number>(1);
  const itemsPerPage = 10;
  const totalPages = Math.ceil(count / itemsPerPage);

  useEffect(() => {
    dispatch(fetchInvoices({ page: currentPage - 1, per_page: itemsPerPage }));
  }, [dispatch, currentPage, itemsPerPage]);

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<number | null>(
    null,
  );

  const handleDeleteInvoice = async () => {
    if (selectedInvoiceId) {
      try {
        await dispatch(deleteInvoiceById(selectedInvoiceId)).unwrap();
        setIsModalOpen(false);
		dispatch(fetchInvoices({ page: currentPage - 1, per_page: itemsPerPage }));
        console.log(t('invoices.toast.invoiceDeletedSuccessfully'));
      } catch (e) {
        console.log(t('invoices.toast.failedToDeleteInvoice'));
      }
    }
  };

  const openDeleteModal = (invoiceId: number) => {
    setSelectedInvoiceId(invoiceId);
    setIsModalOpen(true);
  };

  const columns = [
    { key: 'status', label: t('invoices.status') },
    { key: 'date', label: t('invoices.date') },
    { key: 'client', label: t('invoices.client') },
    { key: 'totalInvoice', label: t('invoices.totalInvoice') },
    { key: 'totalPayslip', label: t('invoices.totalPayslip') },
  ];

  const getActions = (row: any) => {
    const hasDocument =
      row.originalInvoice?.invoiceDocId > 0 ||
      row.originalInvoice?.payslipDocId > 0;

    const actions = [];

    if (!hasDocument) {
      actions.push({
        label: t('invoices.editInvoice'),
        onClick: () => navigate(`/invoices/edit/${row.id}`),
        disabled: Boolean(row.originalInvoice?.placementNumber),
      });
    }

    actions.push({
      label: t('invoices.deleteInvoice'),
      onClick: () => openDeleteModal(row.id),
      className: 'text-red-600 hover:bg-red-100',
    });

    if (row.originalInvoice?.invoiceDocId > 0) {
      actions.push({
        label: t('invoices.downloadInvoice'),
        onClick: () =>
          saveInvoiceDocDownload(
            row.originalInvoice.invoiceDocId,
            `invoice_${row.id}.pdf`,
          ),
      });
    }

    if (row.originalInvoice?.payslipDocId > 0) {
      actions.push({
        label: t('invoices.downloadPayslip'),
        onClick: () =>
          saveInvoiceDocDownload(
            row.originalInvoice.payslipDocId,
            `payslip_${row.id}.pdf`,
          ),
      });
    }

    return actions;
  };

  return (
    <>
      <PageShell
        title={t('invoices.invoices')}
        actions={
          <Button
            label={t('invoices.newInvoice')}
            onClick={() => navigate('/invoices/create-new')}
            fullWidth
          />
        }
      >

        {getInvoicesLoading && (
          <p className="text-center text-gray-500">
            {t('invoices.loadingInvoices')}
          </p>
        )}

        {error && <p className="text-center text-red-500">{error}</p>}

        {!getInvoicesLoading && !error && invoices.length > 0 ? (
          <Table
            data={invoices.map((invoice) => ({
				id: invoice.id,
				status: invoice.status || 'No status',
				date: (() => {
					const isValidSentDate = invoice.invoiceSentDate && 
						typeof invoice.invoiceSentDate === 'string' && 
						invoice.invoiceSentDate.trim() !== '' && 
						invoice.invoiceSentDate !== '0001-01-01T00:00:00Z' &&
						!isNaN(new Date(invoice.invoiceSentDate).getTime());
					
					const formatDate = (date: Date) => {
						const day = String(date.getDate()).padStart(2, '0');
						const month = String(date.getMonth() + 1).padStart(2, '0');
						const year = date.getFullYear();
						return `${day}.${month}.${year}`;
					};

					return isValidSentDate
						? formatDate(new Date(invoice.invoiceSentDate))
						: formatDate(new Date(invoice.createdAt));
				})(),
				client: invoice.clientName || 'N/A',
				totalInvoice: invoice.currency + ' ' + formatSwissAmount(invoice.amountInclVAT) || '0.00',
				totalPayslip: invoice.payslipAmount != '' ? invoice.currency + ' ' + formatSwissAmount(invoice.payslipAmount) : '-',
				originalInvoice: invoice,
				invoiceNumber: invoice.invoiceNumber,
				invoiceDocId: invoice.invoiceDocId,
				payslipDocId: invoice.payslipDocId,
				placementNumber: invoice.placementNumber
            }))}
            columns={columns}
            searchField={true}
            searchFunctionality={true}
            actions={getActions}
            currentPage={currentPage}
            totalPages={Math.ceil(count / itemsPerPage)}
            onPageChange={handlePageChange}
			statusSearchDropdown={true}
			cardLayout={{
				title: 'client',
				aside: 'date',
				status: 'status',
				primary: 'totalInvoice',
				secondary: 'totalPayslip',
			}}
          />
        ) : (
          !getInvoicesLoading && (
            <p className="text-center text-gray-500">
              {t('invoices.noInvoicesFound')}
            </p>
          )
        )}
      </PageShell>
      <ConfirmationModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onConfirm={handleDeleteInvoice}
      />
    </>
  );
};
